import { getSubunitMultiplier } from '@bbpos/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  Input,
  DataTable,
  Skeleton,
  StatCard,
  EmptyState,
  Tabs,
  Modal,
  RadioGroup,
  ConfirmDialog,
  Timeline,
  CreditAccountCard,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, TimelineItem } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import {
  useCreditAccount,
  useUpdateCreditAccount,
  useCreditAccountLedger,
  useCreditInvoices,
  useCreditAccountAging,
  useApplyPayment,
  InvoiceStatusBadge,
  LedgerTypeBadge,
  LedgerReference,
  AccountStatusPill,
} from '@/features/credits';
import type { LedgerEntry, CreditInvoice, PaymentMethod } from '@/features/credits';

type TabId = 'overview' | 'ledger' | 'invoices' | 'payments';
const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'ledger', label: 'Ledger' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'payments', label: 'Payments' },
];

function formatDateIN(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function CreditAccountDetailContent() {
  const dial = useTenantDial();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const flags = useFeatureFlag();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canManage = roleLevel >= ROLE_LEVELS.OWNER;

  const { data: account, isLoading } = useCreditAccount(id);
  const updateMutation = useUpdateCreditAccount();
  const paymentMutation = useApplyPayment();

  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Edit state (rupee input)
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editLimitRupees, setEditLimitRupees] = useState('');
  const [showFreeze, setShowFreeze] = useState(false);

  // Payment modal
  const [payInvoiceId, setPayInvoiceId] = useState<string | null>(null);
  const [payAmountRupees, setPayAmountRupees] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('CASH');
  const [payRef, setPayRef] = useState('');

  // Ledger / Invoices / Aging
  const { data: ledgerData, isLoading: ledgerLoading } = useCreditAccountLedger(activeTab === 'ledger' ? id : undefined, { page: 1 });
  const { data: invoicesData, isLoading: invoicesLoading } = useCreditInvoices(activeTab === 'invoices' || activeTab === 'payments' ? { account_id: id } : { account_id: undefined });
  const { data: agingData } = useCreditAccountAging(id);

  useEffect(() => {
    if (!flags.ENABLE_CREDIT_BILLING) navigate('/dashboard', { replace: true });
  }, [flags, navigate]);

  useEffect(() => {
    if (account) {
      setEditName(account.customer_name);
      setEditPhone(account.customer_phone ?? '');
      setEditLimitRupees(String(account.credit_limit / getSubunitMultiplier(currency)));
    }
  }, [account]);

  const utilPct = useMemo(() => {
    if (!account || account.credit_limit === 0) return 0;
    return Math.round((account.outstanding_balance / account.credit_limit) * 100);
  }, [account]);

  const heroGradient = utilPct > 90 || (account && !account.is_active)
    ? 'from-danger-500 via-red-500 to-orange-500'
    : utilPct > 60
      ? 'from-amber-500 via-orange-500 to-yellow-500'
      : 'from-emerald-500 via-teal-500 to-cyan-500';

  const heroShadow = utilPct > 90 || (account && !account.is_active)
    ? 'shadow-[0_25px_70px_-25px_rgba(239,68,68,0.7)]'
    : utilPct > 60
      ? 'shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]'
      : 'shadow-[0_25px_70px_-25px_rgba(16,185,129,0.7)]';

  const handleSaveEdit = useCallback(() => {
    if (!id) return;
    const limitPaise = editLimitRupees
      ? Math.round(Number(editLimitRupees) * getSubunitMultiplier(currency))
      : undefined;
    if (editPhone && !/^\+[1-9]\d{6,14}$/.test(editPhone)) {
      toast({ variant: 'warning', title: 'Invalid phone', description: 'Enter a valid phone number' }); return;
    }
    updateMutation.mutate(
      { id, input: { customer_name: editName.trim() || undefined, customer_phone: editPhone || undefined, credit_limit: limitPaise } },
      {
        onSuccess: () => { toast({ variant: 'success', title: 'Account updated' }); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [id, editName, editPhone, editLimitRupees, updateMutation, toast]);

  const handleFreeze = useCallback(() => {
    if (!id) return;
    updateMutation.mutate(
      { id, input: { is_active: false } },
      {
        onSuccess: () => { toast({ variant: 'success', title: 'Account frozen' }); setShowFreeze(false); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [id, updateMutation, toast]);

  const handlePayment = useCallback(() => {
    if (!payInvoiceId) return;
    const amtPaise = Math.round(Number(payAmountRupees) * getSubunitMultiplier(currency));
    if (!Number.isFinite(amtPaise) || amtPaise <= 0) { toast({ variant: 'warning', title: 'Invalid amount' }); return; }
    paymentMutation.mutate(
      { invoiceId: payInvoiceId, input: { amount: amtPaise, payment_method: payMethod, reference_number: payRef.trim() || undefined } },
      {
        onSuccess: () => { toast({ variant: 'success', title: 'Payment recorded' }); setPayInvoiceId(null); setPayAmountRupees(''); setPayRef(''); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [payInvoiceId, payAmountRupees, payMethod, payRef, paymentMutation, toast]);

  // Build timeline items from account events
  const timelineItems = useMemo<TimelineItem[]>(() => {
    if (!account) return [];
    const items: TimelineItem[] = [
      { id: 'created', timestamp: account.created_at, action: 'Account created', type: 'completed' as const, description: `Credit limit set to ${account.credit_limit === 0 ? 'Unlimited' : formatPaise(account.credit_limit, currency)}` },
    ];
    if (!account.is_active) {
      items.push({ id: 'frozen', timestamp: account.updated_at ?? account.created_at, action: 'Account frozen', type: 'danger' as const, description: 'New credit sales blocked' });
    }
    if (account.last_payment_date) {
      items.push({ id: 'last-payment', timestamp: account.last_payment_date, action: 'Last payment received', type: 'success' as const });
    }
    return items.sort((a, b) => new Date(b.timestamp as string).getTime() - new Date(a.timestamp as string).getTime());
  }, [account, currency]);

  const ledgerColumns: ColumnDef<LedgerEntry>[] = [
    { key: 'type', header: 'Type', render: (_v, row) => <LedgerTypeBadge type={row.type} /> },
    { key: 'amount', header: 'Amount', numeric: true, render: (_v, row) => <span className={`text-sm font-bold tabular-nums ${row.amount > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'}`}>{row.amount > 0 ? '+' : '-'}{formatPaise(Math.abs(row.amount), currency)}</span> },
    { key: 'balance_after', header: 'Balance', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums">{formatPaise(row.balance_after, currency)}</span> },
    { key: 'reference_id', header: 'Reference', render: (_v, row) => <LedgerReference entry={row} /> },
    { key: 'created_at', header: 'Date', render: (_v, row) => <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{formatDateIN(row.created_at)}</span> },
  ];

  const invoiceColumns: ColumnDef<CreditInvoice>[] = [
    { key: 'invoice_number', header: 'Invoice #', render: (_v, row) => <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.invoice_number}</span> },
    { key: 'amount', header: 'Amount', numeric: true, render: (_v, row) => <span className="text-sm font-bold tabular-nums">{formatPaise(row.amount, currency)}</span> },
    { key: 'paid_amount', header: 'Paid', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums text-success-600 dark:text-success-400">{formatPaise(row.paid_amount, currency)}</span> },
    { key: 'balance', header: 'Balance', numeric: true, render: (_v, row) => <span className={`text-sm font-bold tabular-nums ${row.balance > 0 ? 'text-danger-600 dark:text-danger-400' : ''}`}>{formatPaise(row.balance, currency)}</span> },
    { key: 'status', header: 'Status', render: (_v, row) => <InvoiceStatusBadge status={row.status} /> },
    { key: 'due_date', header: 'Due', render: (_v, row) => <span className={`text-xs tabular-nums ${row.due_date && new Date(row.due_date) < new Date() && row.balance > 0 ? 'text-danger-600 dark:text-danger-400 font-bold' : 'text-neutral-500 dark:text-neutral-400'}`}>{formatDateIN(row.due_date)}</span> },
    ...(canManage ? [{ key: 'actions' as keyof CreditInvoice, header: '', render: (_v: unknown, row: CreditInvoice) => row.status !== 'PAID' && row.status !== 'WRITTEN_OFF' ? <Button variant="secondary" onClick={() => { setPayInvoiceId(row.id); setPayAmountRupees(''); setPayRef(''); }} className="text-xs px-2 py-1">Pay</Button> : null }] : []),
  ];

  // Payments tab — filter invoices to show only payment-relevant rows
  const paymentInvoices = useMemo(() => (invoicesData?.data ?? []).filter((inv) => inv.paid_amount > 0), [invoicesData]);

  if (!flags.ENABLE_CREDIT_BILLING) return null;

  if (isLoading) {
    return <div className="space-y-4"><Skeleton variant="rect" height={180} className="rounded-2xl" /><div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rect" height={100} className="rounded-xl" />)}</div></div>;
  }

  if (!account) {
    return <EmptyState variant="page" title="Account not found" description="This credit account does not exist or you don't have access." />;
  }

  const available = account.credit_limit === 0 ? null : account.credit_limit - account.outstanding_balance;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${heroGradient} p-6 sm:p-7 text-white mb-6 ${heroShadow}`}>
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <AccountStatusPill status={account.status} />
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">{account.customer_name}</h1>
            {account.customer_phone ? <p className="mt-1 text-sm text-white/80 font-mono">{account.customer_phone}</p> : null}
          </div>
          {canManage ? (
            <div className="flex flex-wrap gap-2">
              {account.is_active ? (
                <Button variant="secondary" onClick={() => setShowFreeze(true)} className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm">
                  Freeze Account
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => updateMutation.mutate({ id: id!, input: { is_active: true } }, { onSuccess: () => toast({ variant: 'success', title: 'Account reactivated' }) })} className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm">
                  Reactivate
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* KPI Cards — using StatCard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Credit Limit"
          value={account.credit_limit === 0 ? 'Unlimited' : formatPaise(account.credit_limit, currency)}
          variant="default"
          icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
        />
        <StatCard
          title="Outstanding"
          value={formatPaise(account.outstanding_balance, currency)}
          variant="default"
          icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          anomaly={utilPct > 90}
        />
        <StatCard
          title="Available"
          value={available == null ? 'Unlimited' : formatPaise(available, currency)}
          variant="default"
        />
        <StatCard
          title="Utilization"
          value={account.credit_limit === 0 ? '—' : `${utilPct}%`}
          variant="default"
          anomaly={utilPct > 90}
        />
      </div>

      {/* Tabs */}
      <div className="rounded-xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-4 mb-6">
        <Tabs items={TABS.map((t) => ({ id: t.id, label: t.label }))} activeId={activeTab} onChange={(tid) => setActiveTab(tid as TabId)} />
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Left: Account Details + CreditAccountCard */}
          <div className="space-y-6">
            <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-6">
              <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-5 border-l-4 border-emerald-500 pl-3">Account Details</h2>
              <div className="space-y-4 max-w-lg">
                <Input label="Customer Name" value={editName} onChange={(v) => setEditName(v.slice(0, 200))} />
                <Input label="Phone" variant="phone" defaultDial={dial} value={editPhone} onChange={setEditPhone} placeholder="98765 43210" />
                <Input label={`Credit Limit (${getCurrencySymbol(currency)})`} type="number" min={0} value={editLimitRupees} onChange={setEditLimitRupees} helperText="Enter amount in rupees. 0 = unlimited." />
                {canManage ? <Button variant="primary" onClick={handleSaveEdit} loading={updateMutation.isPending}>Save Changes</Button> : null}
              </div>
            </div>

            {/* CreditAccountCard from @bbpos/ui — visual gauge + aging */}
            {agingData ? (
              <CreditAccountCard
                customer={{ name: account.customer_name, phone: account.customer_phone ?? '' }}
                creditLimit={account.credit_limit}
                outstanding={account.outstanding_balance}
                overdue={account.status === 'OVERDUE'}
                aging={{
                  current: agingData.aging.current,
                  days30: agingData.aging.days_1_30,
                  days60: agingData.aging.days_31_60,
                  days90plus: agingData.aging.days_61_90 + agingData.aging.days_90_plus,
                }}
                currency={currency}
              />
            ) : null}
          </div>

          {/* Right: Timeline */}
          <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-5 border-l-4 border-emerald-500 pl-3">Account Activity</h2>
            <Timeline items={timelineItems} variant="default" maxItems={10} />
          </div>
        </div>
      ) : activeTab === 'ledger' ? (
        <div className="animate-fade-in">
          {ledgerLoading ? (
            <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
          ) : (ledgerData?.data.entries.length ?? 0) === 0 ? (
            <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-10"><EmptyState variant="page" title="No transactions" description="Sales and payments will appear here as they occur." icon={<svg className="h-20 w-20 text-emerald-300 dark:text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>} /></div>
          ) : (
            <section className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md overflow-hidden">
              <DataTable<LedgerEntry> columns={ledgerColumns} data={ledgerData?.data.entries ?? []} totalCount={ledgerData?.meta.total} currentPage={1} pageSize={20} stickyHeader />
            </section>
          )}
        </div>
      ) : activeTab === 'invoices' ? (
        <div className="animate-fade-in">
          {invoicesLoading ? (
            <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
          ) : (invoicesData?.data.length ?? 0) === 0 ? (
            <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-10"><EmptyState variant="page" title="No invoices" description="Credit invoices for this account will appear here." icon={<svg className="h-20 w-20 text-emerald-300 dark:text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>} /></div>
          ) : (
            <section className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md overflow-hidden">
              <DataTable<CreditInvoice> columns={invoiceColumns} data={invoicesData?.data ?? []} totalCount={invoicesData?.meta.total} currentPage={1} pageSize={20} stickyHeader />
            </section>
          )}
        </div>
      ) : activeTab === 'payments' ? (
        <div className="animate-fade-in">
          {invoicesLoading ? (
            <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
          ) : paymentInvoices.length === 0 ? (
            <div className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md p-10"><EmptyState variant="page" title="No payments yet" description="Payments against invoices will appear here." icon={<svg className="h-20 w-20 text-emerald-300 dark:text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} /></div>
          ) : (
            <section className="rounded-2xl backdrop-blur-sm bg-white/80 dark:bg-neutral-800/80 shadow-md overflow-hidden">
              <DataTable<CreditInvoice>
                columns={[
                  { key: 'invoice_number', header: 'Invoice #', render: (_v, row) => <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.invoice_number}</span> },
                  { key: 'amount', header: 'Invoice Amount', numeric: true, render: (_v, row) => <span className="text-sm tabular-nums">{formatPaise(row.amount, currency)}</span> },
                  { key: 'paid_amount', header: 'Paid', numeric: true, render: (_v, row) => <span className="text-sm font-bold tabular-nums text-success-600 dark:text-success-400">{formatPaise(row.paid_amount, currency)}</span> },
                  { key: 'balance', header: 'Remaining', numeric: true, render: (_v, row) => <span className={`text-sm tabular-nums ${row.balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-500'}`}>{formatPaise(row.balance, currency)}</span> },
                  { key: 'status', header: 'Status', render: (_v, row) => <InvoiceStatusBadge status={row.status} /> },
                  ...(canManage ? [{ key: 'actions' as keyof CreditInvoice, header: '', render: (_v: unknown, row: CreditInvoice) => row.status !== 'PAID' && row.status !== 'WRITTEN_OFF' ? <Button variant="secondary" onClick={() => { setPayInvoiceId(row.id); setPayAmountRupees(''); setPayRef(''); }} className="text-xs px-2 py-1">Pay</Button> : null }] : []),
                ]}
                data={paymentInvoices}
                stickyHeader
              />
            </section>
          )}
        </div>
      ) : null}

      {/* Payment Modal — accepts rupees */}
      <Modal open={Boolean(payInvoiceId)} onClose={() => setPayInvoiceId(null)} title="Record Payment" variant="wide">
        <div className="space-y-4">
          <Input label={`Amount (${getCurrencySymbol(currency)})`} type="number" min={0.01} step={0.01} value={payAmountRupees} onChange={setPayAmountRupees} helperText="Enter amount in rupees" required />
          <RadioGroup label="Payment Method" value={payMethod} onChange={(v) => setPayMethod(v as PaymentMethod)} variant="horizontal"
            options={[
              { value: 'CASH', label: 'Cash' },
              { value: 'UPI', label: 'UPI' },
              { value: 'CARD', label: 'Card' },
              { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
            ]}
          />
          <Input label="Reference Number" value={payRef} onChange={setPayRef} placeholder="TXN-12345 (optional)" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setPayInvoiceId(null)}>Cancel</Button>
            <Button variant="primary" onClick={handlePayment} loading={paymentMutation.isPending}>Record Payment</Button>
          </div>
        </div>
      </Modal>

      {/* Freeze Confirm */}
      <ConfirmDialog open={showFreeze} onCancel={() => setShowFreeze(false)} onConfirm={handleFreeze}
        title="Freeze Account" description="This will prevent new credit sales against this account. Existing invoices remain collectible."
        variant="danger" loading={updateMutation.isPending} confirmLabel="Freeze" />
    </div>
  );
}

export function CreditAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: account } = useCreditAccount(id);
  return (
    <PageContainer
      title="Credit Account"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Credit Accounts', href: '/credit-accounts' },
        { label: account?.customer_name ?? 'Detail' },
      ]}
    >
      <ErrorBoundary resetKey={`credit-account-${id}`}>
        <CreditAccountDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
